/* -----------------Globals--------------- */
import { useEffect, useState } from "react";

import { parseAppConfig } from "@helpers/app-config";
import { buildType } from "@helpers/expo-extras";
import { isWeb } from "@helpers/platform";
import { ENDPOINT_APP_CONFIG } from "@memorang/constants/urls";
import { retrieveKey } from "@memorang/helpers/storage";
/* -----------------Helpers & Hooks--------------- */
import axios from "axios";
import { useNavigationContainerRef } from "expo-router";

/* -----------------Child components--------------- */
import * as Font from "expo-font";
import * as SplashScreen from "expo-splash-screen";

import { reactNavigationIntegration } from "@helpers/lib/sentry";
import { captureErrorToSentry } from "@helpers/sentry-logger";
import { customFonts } from "../../assets";
import { setupAudioPlayer } from "../libs/audio-player";
/* -----------------Others--------------- */
import { OneSignal } from "../libs/onesignal";
import {
	isTransientAppConfigFetchError,
	serializeAppConfigFetchError,
} from "./cached-resources-helpers";
import { useAppStore } from "./useAppStore";

const normalizeAppConfigError = (error: unknown) => {
	if (!(axios.isAxiosError(error) && error.response)) {
		return error;
	}

	return Object.assign(
		new Error(
			`App config request error for build type "${buildType}": HTTP ${error.response.status} ${error.message}`,
		),
		{ code: String(error.response.status) },
	);
};

export default function useCachedResources() {
	const [isLoadingComplete, setLoadingComplete] = useState(false);
	const [isDarkMode, setIsDarkMode] = useState(false);

	const oneSignalAppId = useAppStore((store) => store.app.keys.oneSignalAppId);
	const setAppConfig = useAppStore((store) => store.setAppConfig);

	const ref = useNavigationContainerRef();

	useEffect(() => {
		setupAudioPlayer().catch((error) => {
			console.warn("[audio-player] Failed to initialize", error);
		});
	}, []);

	useEffect(() => {
		if (ref && !isWeb) {
			reactNavigationIntegration.registerNavigationContainer(ref);
		}
	}, [ref]);

	useEffect(() => {
		async function loadResourcesAndDataAsync() {
			try {
				SplashScreen.preventAutoHideAsync();
				// Load fonts
				await Font.loadAsync(customFonts);
				const darkMode = retrieveKey("isDarkMode");
				setIsDarkMode(darkMode as boolean);
				if (oneSignalAppId && OneSignal) {
					OneSignal.initialize(oneSignalAppId);
					OneSignal.Notifications.requestPermission(true);
				}
			} catch (e) {
				console.warn(e);
			} finally {
				setLoadingComplete(true);
				SplashScreen.hideAsync();
			}
		}

		loadResourcesAndDataAsync();
	}, [oneSignalAppId]);

	useEffect(() => {
		(async () => {
			try {
				const { data } = await axios.get(`${ENDPOINT_APP_CONFIG}/${buildType}`);
				const parsedData = parseAppConfig(data);

				if (!parsedData) {
					throw new Error(`No app config found for build type "${buildType}"`);
				}
				setAppConfig(parsedData);
			} catch (error) {
				const appConfigError = normalizeAppConfigError(error);

				if (isTransientAppConfigFetchError(appConfigError)) {
					console.warn(
						"[app-config] Failed to fetch remote app config, using bundled defaults",
						appConfigError,
					);
					return;
				}

				console.error("❌ Error fetching custom app config:", appConfigError);
				const errorObj = new Error("Failed to fetch app config");
				captureErrorToSentry(errorObj, {
					buildType,
					...serializeAppConfigFetchError(appConfigError),
				});
			} finally {
				// setIsLoadingConfig(false);
			}
		})();
	}, [setAppConfig]);

	return {
		isLoadingComplete,
		isDarkMode,
	};
}
